import { useState } from 'react';
import { Bot, Tag, Wrench, ChevronDown, ChevronUp, Pencil, Check, Layers } from 'lucide-react';
import { FOREST_GREEN, MID_GRAY, BORDER, SHADOWS, WHITE } from '@/styles/tokens';
import type { ProposedAgent } from '@/types/plan';

interface AgentPreviewCardProps {
  agent: ProposedAgent;
  onUpdate: (agent: ProposedAgent) => void;
  onCreateDraft: () => void;
  onCreateAndActivate: () => void;
  isCreating?: boolean;
}

export function AgentPreviewCard({
  agent,
  onUpdate,
  onCreateDraft,
  onCreateAndActivate,
  isCreating,
}: AgentPreviewCardProps) {
  const [toolsExpanded, setToolsExpanded] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  const startEdit = (field: string, value: string) => {
    setEditingField(field);
    setEditValue(value);
  };

  const commitEdit = (field: keyof ProposedAgent) => {
    onUpdate({ ...agent, [field]: editValue });
    setEditingField(null);
  };

  const domainColors: Record<string, string> = {
    data: '#3B82F6',
    workflow: '#8B5CF6',
    monitoring: '#F59E0B',
    general: MID_GRAY.DEFAULT,
  };

  return (
    <div
      style={{
        border: `1px solid ${BORDER.DEFAULT}`,
        borderRadius: 8,
        background: WHITE,
        boxShadow: SHADOWS.sm,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '12px 16px',
          background: `${FOREST_GREEN.DEFAULT}08`,
          borderBottom: `1px solid ${BORDER.DEFAULT}`,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <Bot style={{ width: 16, height: 16, color: FOREST_GREEN.DEFAULT }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: FOREST_GREEN.DEFAULT }}>
          Agent Preview
        </span>
        {agent.tier != null && (
          <span
            style={{
              marginLeft: 'auto',
              fontSize: 11,
              color: MID_GRAY.DEFAULT,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <Layers style={{ width: 10, height: 10 }} />
            Tier {agent.tier}
          </span>
        )}
      </div>

      {/* Body */}
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Name */}
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: MID_GRAY.DEFAULT, textTransform: 'uppercase', marginBottom: 4, display: 'block' }}>
            Name
          </label>
          {editingField === 'name' ? (
            <div style={{ display: 'flex', gap: 4 }}>
              <input
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                autoFocus
                style={{ flex: 1, padding: '4px 8px', borderRadius: 4, border: `1px solid ${BORDER.DEFAULT}`, fontSize: 14 }}
                onKeyDown={(e) => e.key === 'Enter' && commitEdit('name')}
              />
              <button onClick={() => commitEdit('name')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                <Check style={{ width: 14, height: 14, color: FOREST_GREEN.DEFAULT }} />
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 14, fontWeight: 500 }}>{agent.name}</span>
              <button
                onClick={() => startEdit('name', agent.name)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, opacity: 0.5 }}
              >
                <Pencil style={{ width: 12, height: 12 }} />
              </button>
            </div>
          )}
        </div>

        {/* Description */}
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: MID_GRAY.DEFAULT, textTransform: 'uppercase', marginBottom: 4, display: 'block' }}>
            Description
          </label>
          {editingField === 'description' ? (
            <div style={{ display: 'flex', gap: 4 }}>
              <input
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                autoFocus
                style={{ flex: 1, padding: '4px 8px', borderRadius: 4, border: `1px solid ${BORDER.DEFAULT}`, fontSize: 13 }}
                onKeyDown={(e) => e.key === 'Enter' && commitEdit('description')}
              />
              <button onClick={() => commitEdit('description')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                <Check style={{ width: 14, height: 14, color: FOREST_GREEN.DEFAULT }} />
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 13, color: MID_GRAY.dark }}>{agent.description}</span>
              <button
                onClick={() => startEdit('description', agent.description)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, opacity: 0.5 }}
              >
                <Pencil style={{ width: 12, height: 12 }} />
              </button>
            </div>
          )}
        </div>

        {/* Role + Domain row */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {agent.role && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                padding: '2px 8px',
                borderRadius: 10,
                background: `${FOREST_GREEN.DEFAULT}15`,
                color: FOREST_GREEN.DEFAULT,
              }}
            >
              {agent.role}
            </span>
          )}
          {agent.domain && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                padding: '2px 8px',
                borderRadius: 10,
                background: `${domainColors[agent.domain] || MID_GRAY.DEFAULT}15`,
                color: domainColors[agent.domain] || MID_GRAY.DEFAULT,
              }}
            >
              {agent.domain}
            </span>
          )}
        </div>

        {/* Capabilities */}
        {agent.capabilities && agent.capabilities.length > 0 && (
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: MID_GRAY.DEFAULT, textTransform: 'uppercase', marginBottom: 4, display: 'block' }}>
              Capabilities
            </label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {agent.capabilities.map((cap) => (
                <span
                  key={cap}
                  style={{
                    fontSize: 11,
                    padding: '2px 6px',
                    borderRadius: 10,
                    background: `${MID_GRAY.DEFAULT}10`,
                    color: MID_GRAY.DEFAULT,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                  }}
                >
                  <Tag style={{ width: 10, height: 10 }} />
                  {cap}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Tools (collapsible) */}
        {agent.tools && agent.tools.length > 0 && (
          <div>
            <button
              onClick={() => setToolsExpanded(!toolsExpanded)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: 600,
                color: MID_GRAY.DEFAULT,
                textTransform: 'uppercase',
                padding: 0,
                marginBottom: 4,
              }}
            >
              Tools ({agent.tools.length})
              {toolsExpanded
                ? <ChevronUp style={{ width: 12, height: 12 }} />
                : <ChevronDown style={{ width: 12, height: 12 }} />
              }
            </button>
            {toolsExpanded && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {agent.tools.map((tool, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 12,
                      color: MID_GRAY.dark,
                    }}
                  >
                    <Wrench style={{ width: 12, height: 12, color: MID_GRAY.DEFAULT }} />
                    <span>{tool}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div
        style={{
          padding: '12px 16px',
          borderTop: `1px solid ${BORDER.DEFAULT}`,
          display: 'flex',
          gap: 8,
          justifyContent: 'flex-end',
        }}
      >
        <button
          onClick={onCreateDraft}
          disabled={isCreating}
          style={{
            padding: '6px 14px',
            borderRadius: 6,
            border: `1px solid ${BORDER.DEFAULT}`,
            background: WHITE,
            fontSize: 13,
            fontWeight: 500,
            cursor: isCreating ? 'not-allowed' : 'pointer',
            opacity: isCreating ? 0.6 : 1,
          }}
        >
          Create as Draft
        </button>
        <button
          onClick={onCreateAndActivate}
          disabled={isCreating}
          style={{
            padding: '6px 14px',
            borderRadius: 6,
            border: 'none',
            background: FOREST_GREEN.DEFAULT,
            color: WHITE,
            fontSize: 13,
            fontWeight: 500,
            cursor: isCreating ? 'not-allowed' : 'pointer',
            opacity: isCreating ? 0.6 : 1,
          }}
        >
          {isCreating ? 'Creating...' : 'Create & Activate'}
        </button>
      </div>
    </div>
  );
}
